import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import barcode from "./barcode.png";
import { ADD_AUDIT_LOG } from "../../Mutations/AddAuditLog";
import { useMutation } from "@apollo/client";

const IGNORED_MODIFIER_KEYS = new Set([
  "Alt",
  "AltGraph",
  "CapsLock",
  "Clear",
  "Control",
  "Fn",
  "FnLock",
  "Meta",
  "NumLock",
  "ScrollLock",
  "Shift",
]);

const ScanInput = ({
  id,
  inputRef,
  inputDisabled,
  submitDisabled,
  clearDisabled,
  submitCallback,
  onClearCallback,
  keyData,
  setKeyData,
  isParsing,
  error,
  user,
  incidentId = "",
  submitted = false,
  wait = false,
  setWait,
}) => {
  const [auditLogKeys, setAuditLogKeys] = useState([]);

  let timeoutId = useRef(null);
  useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  const localClear = () => {
    setAuditLogKeys([]);
    onClearCallback();
  };

  const [addAuditLog] = useMutation(ADD_AUDIT_LOG, {
  });

  const handleKeyPress = e => {
    if (e?.key === "Escape") {
      return;
    }
    if (e?.key === "Tab") {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    setWait(true); // Set wait to true when the function is called

    // Clear the existing timeout if any
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    // Set a timeout to reset wait to false after 1 second
    timeoutId.current = setTimeout(() => {
      setWait(false);
    }, 1000);
    try {
      // Alphanumeric check using a regular expression
      if (/^[\w\p{P}\p{L}\p{N}\p{S} $]$/u.test(e.key)) {
        // Append alphanumeric characters as they are
        let value = keyData + e.key;
        setKeyData(value);
      } else if (e.key === "ArrowDown") {
        // Leave ArrowDown as a newline character
        let value = keyData + "\n";
        setKeyData(value);
      } else if (e.key === "Enter" || e.key === "F9" || e.key === "F7") {
        // Enter, F9, F7 remain as carriage return characters
        let value = keyData + "\r";
        setKeyData(value);
      } else if (IGNORED_MODIFIER_KEYS.has(e.key)) {
        // Ignore keyboard modifiers that do not contribute scan data.
        return null;
      } else if (e.key === "Backspace") {
        // Handle Backspace by removing the last character
        let value = keyData.slice(0, -1);
        setKeyData(value);
      } else if (
        (e.key === "Unidentified" || e?.keyCode === 0) &&
        (e?.type === "keydown" || e?.type === "keypress")
      ) {
        // This is most likely the Record Separator character (ASCII 30)
        let value = keyData + "\t";
        setKeyData(value);
      } else {
        // For any other key, append a tab character and log an additional audit log entry
        //Add the non-alphanumeric key to an array to be sent to the audit log
        setAuditLogKeys([
          ...auditLogKeys,
          `Non-alphanumeric key pressed: ${e?.key}. Key code: ${e?.keyCode}. `,
        ]);

      }
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
    }
  };

  useEffect(() => {
    const logErrorKeys = () => {
      if (auditLogKeys.length > 0) {
        addAuditLog({
          variables: {
            input: {
              incidentId: incidentId,
              originatorId: user?.id,
              message: auditLogKeys.join(" "),
              action: "Unhandled key in license scan",
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
      }
    };

    if (keyData?.length > 5 && !wait && !submitDisabled && !submitted) {
      submitCallback();
      logErrorKeys();
    }
  }, [
    addAuditLog,
    auditLogKeys,
    incidentId,
    keyData?.length,
    submitCallback,
    submitDisabled,
    submitted,
    user?.id,
    wait,
  ]);

  const submitText =
    keyData === "" ? "Please scan" : wait ? "Processing" : "Submit";

  return (
    <>
      <div>
        <>
          <label htmlFor={id}>
            Scan:
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- Scan dialogs focus this input when opened so barcode keystrokes are captured immediately. */}
            <input autoFocus
              id={id}
              onKeyDown={handleKeyPress}
              type="text"
              ref={inputRef}
              disabled={inputDisabled}
            />
          </label>
          <input
            type="button"
            data-testid="submit-button"
            disabled={submitDisabled || wait}
            onClick={submitCallback}
            value={submitText}
          />
        </>
        <input
          type="button"
          disabled={clearDisabled || wait}
          onClick={localClear}
          value="Clear"
        />
      </div>
      <div>
        {wait && <span>Scanning the barcode...</span>}
        {isParsing && <span>Decoding the scanned information...</span>}
        {error && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ color: "red" }}>
              This scan is missing necessary information. The scan has been
              added to the audit log. Please close this modal and enter the
              information manually.
            </span>
            <span style={{ color: "red" }}>
              If this problem persists please scan the barcode below to ensure
              your scanner is configured correctly.
            </span>
            <img
              height={140}
              width={300}
              src={barcode}
              alt="USB Keyboard barcode for the L-Tron scanner"
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ScanInput;
